import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CALENDAR } from '@flexo/reducers';

import { HiveCalendarContentDay } from './hive-calendar-content-day';
import { HiveCalendarContentMonth } from './hive-calendar-content-month';
import { HiveCalendarContentWeek } from './hive-calendar-content-week';
import { HiveCalendarContentYear } from './hive-calendar-content-year';
import { HiveCalendarInnerInput } from './hive-calendar-inner-input';
import { HiveCalendarInput } from './hive-calendar-input';

import { useOnClickOutside } from '@flexo/hooks';
import styles from './hive-calendar.module.scss';

export function HiveCalendar({
  calendarColor,
  hideInput,
  isAnchorTarget,
}: {
  calendarColor?: string;
  hideInput?: boolean;
  isAnchorTarget?: boolean;
}) {
  const { t } = useTranslation();
  const calendarRef = useRef(null);
  const [isLargeViewport, setIsLargeViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 992;
  });
  const calendarId = isLargeViewport ? 'HiveCalendar' : 'HiveCalendarSmall';

  const dispatch = useDispatch();
  const calendar = useSelector((state: any) => state.calendar);
  const hiveCalendar = calendar.hiveCalendar;
  const {
    selectedTimeSet,
    calendarTimeSet,
    calendarNavigationActive,
    navigationStart,
  } = hiveCalendar;

  const setShowCalendar = (state: boolean) => {
    dispatch(CALENDAR.setShowHiveCalendar(state as any));
  };

  const timeSet = calendarTimeSet || selectedTimeSet;

  function getCalendarContent() {
    switch (timeSet) {
      case 'day':
        return <HiveCalendarContentDay {...{ setShowCalendar }} />;
      case 'week':
        return <HiveCalendarContentWeek {...{ setShowCalendar }} />;
      case 'month':
        return <HiveCalendarContentMonth {...{ setShowCalendar }} />;
      case 'year':
        return <HiveCalendarContentYear {...{ setShowCalendar }} />;
      default:
        return <HiveCalendarContentDay {...{ setShowCalendar }} />;
    }
  }

  function checkActiveTimeSet(_timeSet: string) {
    if (calendarNavigationActive === false) {
      return _timeSet === timeSet;
    } else {
      return _timeSet === navigationStart;
    }
  }

  function isChildOf(event) {
    const parentDiv = document.getElementById(calendarId);

    let targetElement = event.target;

    while (targetElement !== document.body) {
      if (targetElement === parentDiv || targetElement.id === calendarId) {
        return true;
      }
      targetElement = targetElement.parentNode;
    }

    return false; // Il target non è figlio del parentDiv
  }

  function SetTimeSet(newTimeSet: string) {
    dispatch(CALENDAR.setHiveCalendarNavigationInactive());
    dispatch(CALENDAR.setHiveCalendarTimeSet(newTimeSet as any));
    dispatch(CALENDAR.setHiveCalendarNavigationStart(newTimeSet as any));
  }

  useOnClickOutside(calendarRef, (ev: any) => {
    if (isChildOf(ev) === true) {
      return;
    }
    if (hiveCalendar.showHiveCalendar === true) {
      setShowCalendar(false);
      dispatch(CALENDAR.ResetHiveCalendar());
    }
  });

  const [anchorStyle, setAnchorStyle] = useState<
    React.CSSProperties | undefined
  >(undefined);

  useEffect(() => {
    if (!hiveCalendar.anchorPosition) {
      setAnchorStyle(undefined);
      return;
    }

    const updateAnchorPosition = () => {
      // Use clientWidth to exclude scrollbar
      const vw = document.documentElement.clientWidth;

      // Under 992px: center the calendar on screen with 16px margins
      if (vw <= 992) {
        const scrollbarWidth = window.innerWidth - vw;
        setAnchorStyle({
          position: 'fixed',
          top: '96px',
          left: '16px',
          right: `${30}px`,
          width: 'auto',
          maxWidth: 'none',
        });
        return;
      }

      const anchor = document.querySelector(
        '[data-hive-calendar-anchor-active]'
      );
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        const calendarWidth = calendarRef.current?.offsetWidth || 360;
        const rawRight = vw - rect.right;
        // Clamp: at least 16px from both left and right edges
        const clampedRight = Math.max(
          16,
          Math.min(rawRight, vw - calendarWidth - 16)
        );

        setAnchorStyle({
          top: rect.bottom + 4,
          right: clampedRight,
          left: 'auto',
        });
      }
    };

    updateAnchorPosition();
    window.addEventListener('resize', updateAnchorPosition);
    return () => window.removeEventListener('resize', updateAnchorPosition);
  }, [hiveCalendar.anchorPosition]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let rafId: number | null = null;
    const updateViewport = () => {
      const next = window.innerWidth > 992;
      setIsLargeViewport((prev) => (prev !== next ? next : prev));
    };
    const onResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateViewport);
    };
    window.addEventListener('resize', onResize);
    // Ensure state is correct at mount
    updateViewport();
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const isDropdownVisible =
    calendar.hiveCalendar.showHiveCalendar &&
    !(hiveCalendar.anchorPosition && !isAnchorTarget);

  return (
    <div
      className={styles.HiveCalendar__Wrapper}
      style={
        {
          ...( hideInput && !isDropdownVisible ? { pointerEvents: 'none' } : undefined),
          ...( hideInput ? { width: 0 } : {}  )
        }
      }
    >
      {!hideInput && (
        <HiveCalendarInput
          {...{
            setShowCalendar,
            showCalendar: calendar.hiveCalendar.showHiveCalendar,
            calendarColor,
          }}
        />
      )}

      {isDropdownVisible && (
        <div
          id={calendarId}
          className={styles.HiveCalendar__Calendar}
          ref={calendarRef}
          style={anchorStyle}
        >
          <div className={styles.HiveCalendar__Calendar__Buttons__Wrapper}>
            <button
              className={`
            ${styles.HiveCalendar__Calendar__Buttons__Button}
            ${
              checkActiveTimeSet('day')
                ? styles.HiveCalendar__Calendar__Buttons__Button__Active
                : ''
            }
            `}
              onClick={() => SetTimeSet('day')}
            >
              {t('widgets.calendar.actions.day')}
            </button>
            <button
              className={`
            ${styles.HiveCalendar__Calendar__Buttons__Button}
            ${
              checkActiveTimeSet('week')
                ? styles.HiveCalendar__Calendar__Buttons__Button__Active
                : ''
            }
            `}
              onClick={() => SetTimeSet('week' as any)}
            >
              {t('widgets.calendar.actions.week')}
            </button>
            <button
              className={`
            ${styles.HiveCalendar__Calendar__Buttons__Button}
            ${
              checkActiveTimeSet('month')
                ? styles.HiveCalendar__Calendar__Buttons__Button__Active
                : ''
            }
            `}
              onClick={() => SetTimeSet('month' as any)}
            >
              {t('widgets.calendar.actions.month')}
            </button>
            <button
              className={`
            ${styles.HiveCalendar__Calendar__Buttons__Button}
            ${
              checkActiveTimeSet('year')
                ? styles.HiveCalendar__Calendar__Buttons__Button__Active
                : ''
            }
            `}
              onClick={() => SetTimeSet('year' as any)}
            >
              {t('widgets.calendar.actions.year')}
            </button>
          </div>

          <HiveCalendarInnerInput />

          {getCalendarContent()}
        </div>
      )}
    </div>
  );
}
